import { useQuery } from '@tanstack/react-query';
import { http } from 'services/http';
import { INVOICES_GET_MANY, INVOICES_SEARCH_HINTS } from 'api/query-keys.ts';
import type { InvoiceRes } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import type { IInvoicesFilter } from 'shared/stores/use-invoices-filters.ts';
import { setFiltersToQs } from 'shared/utils/filters/set-filters-to-qs.ts';
import type { WithPagination } from 'shared/utils/pagination.ts';
import { preparePagination } from 'shared/utils/pagination.ts';
import type { IWithPagination } from 'types/api-query.ts';

interface IParams extends IWithPagination {
  search?: string;
  filters?: IInvoicesFilter;
  isHints?: boolean;
  isSkip?: boolean;
}

export const useGetManyInvoices = (params?: IParams) => {
  const { limit = PAGINATION_DEFAULT_LIMIT, page = 1, search, filters, isHints = false, isSkip = false } = params || {};

  const qs = new URLSearchParams({ limit: String(limit), page: String(page) });
  if (search) qs.set('filter[search]', search);
  if (filters) setFiltersToQs<IInvoicesFilter>(qs, filters);

  return useQuery<WithPagination<InvoiceRes[]>>({
    queryKey: isHints ? [INVOICES_SEARCH_HINTS, search] : [INVOICES_GET_MANY, page, limit, qs.toString()],
    queryFn: async () => {
      const { data, headers } = await http.get<InvoiceRes[]>(`/v1/invoices?${qs.toString()}`);
      const pagination = preparePagination(headers);

      return { data, pagination };
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: 0,
    enabled: !isSkip,
  });
};
